/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { getUrlSync, IComponentProps, IImageSettings } from '@msdyn365-commerce/core';
import { format } from '@msdyn365-commerce/retail-proxy';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { OrderTemplateProductsPreviewComponent } from '../order-template/order-templates-list/order-template-products-preview';
import { getTotalItems, IAddToCartInput, IItemsAddedToCartResources } from './items-added-to-cart';
import { ItemsAddedErrorComponent } from './items-added-to-cart-error-component';

/**
 * Interface for items added to cart data.
 */
export interface IItemsAddedToCartDialogData {
    products: IAddToCartInput[];
}

/**
 * Interface for items added to cart props.
 */
export interface IItemsAddedToCartDialogProps extends IComponentProps<IItemsAddedToCartDialogData> {
    className: string;
    dialogStrings: IItemsAddedToCartResources;

    imageSettings?: IImageSettings;
    isModalOpen?: boolean;

    onClose(): void;
}

/**
 * Items added to cart dialog.
 * @param dialogProps -- The dialog props.
 * @returns The dialog element.
 */
export const ItemsAddedToCartDialogComponent = (dialogProps: IItemsAddedToCartDialogProps): JSX.Element => {
    const {
        dialogStrings,
        className,
        context,
        isModalOpen,
        imageSettings,
        data: {
            products
        }
    } = dialogProps;

    /**
     * Get cart page URL.
     * @returns The URL of cart page.
     */
    const getCartPageUrl = (): string => {
        return getUrlSync('cart', context.actionContext) ?? '';
    };

    const itemsString = format(dialogStrings.itemsAddedToCartFormatText, getTotalItems(products).toString()) + dialogStrings.itemsAddedToCartHeaderText;
    const linesString = format(dialogStrings.linesAddedToCartFormatText, products.length.toString());

    const fullProducts: FullProduct[] = products.map(item => {
        return { ProductDetails: item.product };
    });

    return (
        <Modal isOpen={isModalOpen} className={className} toggle={dialogProps.onClose}>
            <ModalHeader className={`${className}__dialog__header`} toggle={dialogProps.onClose}>
                <b>
                    {linesString}
                </b>
                <span>
                    {dialogStrings.itemsAddedToCartHeaderText}
                </span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <div className={`${className}__dialog__content`}>
                    <OrderTemplateProductsPreviewComponent
                        context={context}
                        className={`${className}__dialog`}
                        products={fullProducts}
                        imageSettings={imageSettings}
                    />
                    <div className={`${className}__dialog__description`}>
                        <div className={`${className}__dialog__order-template-items`}>
                            {itemsString}
                        </div>
                    </div>
                    <ItemsAddedErrorComponent errorMessage={dialogProps.dialogStrings.itemsAddedValidationErrorMessage} />
                </div>
            </ModalBody>
            <ModalFooter
                className={`${className}__dialog__footer`}
            >
                <Button
                    className={`${className}__dialog__view-cart-button`}
                    title={dialogStrings.viewCartButtonText}
                    href={getCartPageUrl()}
                >
                    {dialogStrings.viewCartButtonText}
                </Button>
                <Button
                    className={`${className}__dialog__continue-shopping`}
                    title={dialogStrings.closeButtonLabel}
                    onClick={dialogProps.onClose}
                >
                    {dialogStrings.closeButtonLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
